// Customer analysis — the pure half (pinned by scripts/customer-analysis.test.mjs).
// Everything is worked out from the customer's own cleans and, where the app
// recorded them, the change log: nothing here is stored, so it can never go
// stale or disagree with the History tab.

import { toCollect, visitReceived } from './brought'
import type { AuditEvent } from './history'
import type { Clean, Customer } from './types'
import { roundDay, currencySymbol } from './round-config'
import { countsAsTimed, minutesBetween, type FirstClean } from './job-times'

// 'yyyy-mm-dd HH:MM:SS' (UTC) → the round's day it fell on.
function stampDay(stamp: string): string {
  const d = new Date(`${stamp.slice(0, 10)}T${stamp.length > 10 ? stamp.slice(11, 19) : '12:00:00'}Z`)
  return Number.isNaN(d.getTime()) ? stamp.slice(0, 10) : roundDay(d)
}

const DAY = 86_400_000
const daysBetween = (a: string, b: string): number => Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / DAY)

export interface Analysis {
  verdict: string
  customerSince: string | null
  lifetimePaidPence: number
  lifetimeCleans: number
  lastYearPaidPence: number
  lastYearCleans: number
  projectedYearPence: number | null     // regular price × visits a year from the agreed cycle
  averagePricePence: number | null
  actualGapDays: number | null          // median gap between done cleans
  agreedCycleDays: number | null
  rhythm: string                        // "every 9 weeks, agreed 8"
  cashCount: number
  bankCount: number
  methodUnknown: number
  daysToPay: number | null              // median, where the app recorded the payment
  daysToPaySample: number
  chasesNeeded: number                  // cleans that needed at least one chase
  chasedCleans: number
  cancelled: number
  couldntDo: number
  quietSpells: number                   // gaps longer than twice the cycle
  longestQuietDays: number | null
  priceHistory: { date: string; pence: number }[]  // each time the done price changed
  owedPence: number
  // Time on the job (Liam, 9 Oct 2026: "time on job great feedback"): from the worker's Start and Finish on done visits.
  jobMinutes: number | null             // median minutes on a timed visit
  jobTimed: number                      // visits with both taps
  perHourPence: number | null           // charged over those visits, per hour on them
  // How they answer (Liam: "the reply rate"): an ask is a first message this cycle; it is answered when a reply is
  // recorded on it in Messages or a visit's day is agreed before the next ask.
  replyAsks: number
  replyAnswered: number
  replyDays: number | null              // median days from the ask to the answer
  // Several jobs (step 2, PLAN 7): one line per job, each read on that job's own visits. Empty unless the customer
  // has more than one job; then rhythm, quiet spells and price history above are the main job's, the projection
  // is every running job's added up, and the money (paid, owed, last 12 months) stays over every visit.
  perJob: JobAnalysis[]
}

export interface JobAnalysis {
  name: string
  stopped: boolean
  agreedCycleDays: number | null
  actualGapDays: number | null
  rhythm: string
  quietSpells: number
  priceHistory: { date: string; pence: number }[]
  projectedYearPence: number | null
}

// A job as the analysis reads it: its id (null = the main job), name, price, how often and whether it runs.
export interface AnalysedJob {
  id: number | null
  name: string
  price_pence: number | null
  cycle_days: number | null
  stopped: boolean
}

function rhythmOf(gap: number | null, cycle: number | null, count: number): string {
  return !gap ? (count ? 'one clean on record' : 'no cleans yet')
    : cycle ? `every ${weeksLabel(gap)}, agreed ${weeksLabel(cycle)}` : `every ${weeksLabel(gap)}, no cycle agreed`
}

function gapsOf(done: Clean[]): number[] {
  const gaps: number[] = []
  for (let i = 1; i < done.length; i++) gaps.push(daysBetween(done[i - 1]!.date, done[i]!.date))
  return gaps
}

// Each time the done price changed, oldest first.
function pricesOf(done: Clean[]): { date: string; pence: number }[] {
  const out: { date: string; pence: number }[] = []
  for (const c of done) {
    if (c.amount_pence === null || c.amount_pence === undefined) continue
    // A visit with extras (0087) is a one-off price, not a change to theirs (Sonnet's extras review 10).
    if (c.extras_json) continue
    if (!out.length || out[out.length - 1]!.pence !== c.amount_pence) out.push({ date: c.date, pence: c.amount_pence })
  }
  return out
}

function median(xs: number[]): number | null {
  if (!xs.length) return null
  const s = [...xs].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid]! : Math.round((s[mid - 1]! + s[mid]!) / 2)
}

export function weeksLabel(days: number | null): string {
  if (!days) return '—'
  if (days < 10) return `${days} days`
  const w = Math.round(days / 7)
  if (w < 9) return w === 1 ? '1 week' : `${w} weeks`
  const m = Math.round(days / 30.4)
  if (m < 24) return m === 1 ? '1 month' : `${m} months`
  const y = Math.round(days / 365)
  return y === 1 ? '1 year' : `${y} years`
}

// When the app recorded a clean turning paid: the change log holds the day.
function paidOnByClean(events: AuditEvent[]): Map<number, string> {
  const out = new Map<number, string>()
  for (const ev of [...events].reverse()) {           // oldest first, keep the first "paid"
    if (ev.entity !== 'cleans' || !ev.after_json) continue
    // Bulk data fixes (the importer, the orchestrator's clean-ups) stamp a
    // day that is not when the customer paid — they would make everyone
    // look months late.
    if (/orchestrator|importer|import/i.test(String(ev.actor ?? ''))) continue
    let after: Record<string, unknown> | null = null
    let before: Record<string, unknown> | null = null
    try { after = JSON.parse(ev.after_json); before = ev.before_json ? JSON.parse(ev.before_json) : null } catch { continue }
    // The round's day it was marked paid, not UTC's (Astra review, 29 Sep 2026).
    if (after?.payment_status === 'paid' && before?.payment_status !== 'paid' && !out.has(ev.entity_id)) out.set(ev.entity_id, stampDay(ev.created_at))
  }
  return out
}

// Asks and answers, oldest first, from the change log (the importer's and clean-up rows left out, as for payments).
// A first message (their count to 1) opens an ask; a reply recorded (message_reply set) or a visit's day agreed answers
// it; the count put back to 0 with no reply ("Didn't send it", Undo) takes the ask back.
function repliesOf(events: AuditEvent[]): { asks: number; answered: number; days: number | null } {
  let asks = 0, answered = 0, open: string | null = null
  const waits: number[] = []
  const answer = (stamp: string): void => { if (!open) return; answered++; waits.push(Math.max(0, daysBetween(open, stampDay(stamp)))); open = null }
  for (const ev of [...events].reverse()) {
    if (/orchestrator|importer|import/i.test(String(ev.actor ?? ''))) continue
    let after: Record<string, unknown> | null = null, before: Record<string, unknown> | null = null
    try { after = ev.after_json ? JSON.parse(ev.after_json) : null; before = ev.before_json ? JSON.parse(ev.before_json) : null } catch { continue }
    if (!after) continue
    if (ev.entity === 'cleans') { if (after.confirmed === 1 && before?.confirmed !== 1) answer(ev.created_at); continue }
    if (ev.entity !== 'customers') continue
    if (after.message_reply && after.message_reply !== (before?.message_reply ?? null)) { answer(ev.created_at); continue }
    if (!('rebook_messages' in after)) continue
    const now = Number(after.rebook_messages ?? 0), was = Number(before?.rebook_messages ?? 0)
    if (now === 1 && was !== 1) { asks++; open = stampDay(ev.created_at) }
    else if (now === 0 && was > 0 && open) { asks--; open = null }
  }
  return { asks, answered, days: median(waits) }
}

// `jobs`: the customer's jobs when they have more than one (several jobs on); left out, everything is worked out
// exactly as before.
export function analyse(customer: Pick<Customer, 'cycle_days' | 'reg_price_pence' | 'status'>, cleans: Clean[], events: AuditEvent[], today: string, jobs: AnalysedJob[] = [], accountOwedPence = 0, firstClean: FirstClean | null = null): Analysis {
  const real = cleans.filter((c) => !c.deleted_at && c.source !== 'tentative')
  const done = real.filter((c) => c.state === 'done').sort((a, b) => a.date.localeCompare(b.date))
  const several = jobs.length > 1
  const jobOf = (c: Clean): number | null => (typeof c.job_id === 'number' ? c.job_id : null)
  // The main job's own visits when there are several: its rhythm is measured against its own cycle.
  const mainDone = several ? done.filter((c) => jobOf(c) === null) : done
  const paid = done.filter((c) => c.payment_status === 'paid')
  const yearAgo = new Date(`${today}T12:00:00`); yearAgo.setFullYear(yearAgo.getFullYear() - 1)
  const yearAgoISO = yearAgo.toISOString().slice(0, 10)
  const lastYear = paid.filter((c) => c.date >= yearAgoISO)
  const sum = (xs: Clean[]): number => xs.reduce((s, c) => s + (c.amount_pence ?? 0), 0)
  // Money actually received and still owed (0043, src/brought.ts): credit used is not received, cash part paid is,
  // and a visit in an opening balance counts in neither. With no brought money, the full visit amounts, as before.
  const received = (xs: Clean[]): number => xs.reduce((s, c) => s + visitReceived(c), 0)
  const stillOwed = (xs: Clean[]): number => xs.reduce((s, c) => s + (c.carryover === 1 ? 0 : (toCollect(c) ?? 0)), 0)
  const lastYearDone = done.filter((c) => c.date >= yearAgoISO)
  const priced = done.filter((c) => c.amount_pence !== null && c.amount_pence !== undefined)

  const gaps = gapsOf(mainDone)
  const actualGap = median(gaps)
  const cycle = customer.cycle_days && customer.cycle_days > 0 ? customer.cycle_days : null
  const quietGaps = cycle ? gaps.filter((g) => g > cycle * 2) : []

  const paidOn = paidOnByClean(events)
  const toPay: number[] = []
  for (const c of paid) { const on = paidOn.get(c.id); if (on) toPay.push(Math.max(0, daysBetween(c.date, on))) }

  // One job: the windows' prices, as always. Several: the main job's own visits, whatever their kind.
  const priceHistory = several ? pricesOf(mainDone) : pricesOf(priced.filter((c) => c.kind === 'window'))

  const chasedCleans = done.filter((c) => c.chased || (c.chase_count ?? 0) > 0).length
  const owed = done.filter((c) => c.payment_status === 'unpaid')
  const perJob: JobAnalysis[] = several
    ? jobs.map((j) => {
        const jd = done.filter((c) => jobOf(c) === j.id)
        const jg = gapsOf(jd)
        const jc = j.cycle_days && j.cycle_days > 0 ? j.cycle_days : null
        const gap = median(jg)
        return {
          name: j.name,
          stopped: j.stopped,
          agreedCycleDays: jc,
          actualGapDays: gap,
          rhythm: rhythmOf(gap, jc, jd.length),
          quietSpells: jc ? jg.filter((g) => g > jc * 2).length : 0,
          priceHistory: pricesOf(jd),
          projectedYearPence: !j.stopped && j.price_pence && jc ? Math.round(j.price_pence * (365 / jc)) : null,
        }
      })
    : []
  // A year at the agreed prices: every running job's, each once (PLAN 7).
  const projected = several
    ? perJob.reduce<number | null>((sum, j) => (j.projectedYearPence === null ? sum : (sum ?? 0) + j.projectedYearPence), null)
    : customer.reg_price_pence && cycle ? Math.round(customer.reg_price_pence * (365 / cycle)) : null

  const rhythm = rhythmOf(actualGap, cycle, mainDone.length)

  // A visit timed for under 5 minutes or over 10 hours is a tap left running or a slip, not the job.
  // A taken-on customer's first clean is left out, as in their usual time (0082; Astra round 1 H6), unless they have no repeat.
  const timed = done.filter((c) => countsAsTimed(c) && !c.extras_json && (!firstClean || firstClean.counts || !firstClean.ids.includes(c.id))).map((c) => ({ m: minutesBetween(c.started_at!, c.finished_at!), p: c.amount_pence ?? null }))
    .filter((t) => t.m >= 5 && t.m <= 600)
  const pricedTimed = timed.filter((t) => t.p !== null)
  const timedMinutes = pricedTimed.reduce((s, t) => s + t.m, 0)
  const replies = repliesOf(events)

  // The one line at the top. Regular = keeps roughly to the agreed cycle;
  // pays on time = median under 8 days where known; the money is last year's
  // paid total, or the projection when there is not a year of data yet.
  const bits: string[] = []
  if (mainDone.length >= 3 && cycle && actualGap) bits.push(actualGap <= cycle * 1.25 ? 'Regular' : actualGap <= cycle * 2 ? 'Slips a little' : 'Irregular')
  else if (done.length) bits.push(done.length === 1 ? 'New' : 'Early days')
  else bits.push('No visits yet')
  const dtp = median(toPay)
  if (dtp !== null) bits.push(dtp <= 7 ? 'pays on time' : dtp <= 21 ? 'pays within a few weeks' : 'slow to pay')
  else if (chasedCleans && done.length) bits.push(chasedCleans / done.length >= 0.5 ? 'often needs chasing' : 'sometimes needs chasing')
  // What they owe: the visits still to pay and, with money brought from Squeegee, their account (the figure Owed shows).
  const owes = stillOwed(owed) + Math.max(0, accountOwedPence)
  if (owes > 0) bits.push(`owes ${currencySymbol()}${(owes / 100).toFixed(2).replace(/\.00$/, '')}`)
  const worth = lastYear.length >= 2 ? received(lastYearDone) : projected
  if (worth) bits.push(`worth about ${currencySymbol()}${Math.round(worth / 100)} a year`)
  if (customer.status !== 'active') bits.unshift(customer.status)

  return {
    verdict: bits.join(', ').replace(/^./, (m) => m.toUpperCase()),
    customerSince: done[0]?.date ?? real.sort((a, b) => a.date.localeCompare(b.date))[0]?.date ?? null,
    lifetimePaidPence: received(done),
    lifetimeCleans: done.length,
    lastYearPaidPence: received(lastYearDone),
    lastYearCleans: lastYear.length,
    projectedYearPence: projected,
    averagePricePence: priced.length ? Math.round(sum(priced) / priced.length) : null,
    actualGapDays: actualGap,
    agreedCycleDays: cycle,
    rhythm,
    cashCount: paid.filter((c) => c.payment_method === 'cash').length,
    bankCount: paid.filter((c) => c.payment_method === 'bank').length,
    methodUnknown: paid.filter((c) => !c.payment_method).length,
    daysToPay: dtp,
    daysToPaySample: toPay.length,
    chasesNeeded: done.reduce((s, c) => s + Math.max(c.chase_count ?? 0, c.chased ? 1 : 0), 0),
    chasedCleans,
    cancelled: real.filter((c) => c.state === 'cancelled').length,
    couldntDo: real.filter((c) => !!c.blocked_reason).length,
    quietSpells: quietGaps.length,
    longestQuietDays: quietGaps.length ? Math.max(...quietGaps) : null,
    priceHistory,
    owedPence: owes,
    jobMinutes: median(timed.map((t) => t.m)),
    jobTimed: timed.length,
    perHourPence: timedMinutes ? Math.round(pricedTimed.reduce((s, t) => s + t.p!, 0) / timedMinutes * 60) : null,
    replyAsks: replies.asks,
    replyAnswered: replies.answered,
    replyDays: replies.days,
    perJob,
  }
}
